import React, { useEffect, useState } from "react";
import axiosInstance from "../../../../../httpInterceptor";
import { toast } from "react-toastify";
import "./CaseStudyNew.scss";
import { Autocomplete, TextField, Checkbox, Chip, Popper } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

interface CaseStudyProps {
  projectid: string | undefined;
  setname: string | undefined;
  pu?: string | undefined;
}

interface ProjectDetails {
  projectid: string;
  set_name: string;
  account: string;
  mapped_industry: string; // normalized mapped industry (never undefined)
  capability_area: string[]; // always array
  capability: string[]; // always array
  du?: string; // delivery unit for fallback mapping
}

const CaseStudy: React.FC<CaseStudyProps> = ({ projectid, setname, pu }) => {
  console.log("[CaseStudy] Component props:", { projectid, setname, pu });
  
  const [caseStudyData, setCaseStudyData] = useState<any[]>([]);
  const [projectDetails, setProjectDetails] = useState<ProjectDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [messageBox, setMessageBox] = useState<{ show: boolean; message: string }>({ show: false, message: "" });

  // Add new states for edit functionality
  const [isEditing, setIsEditing] = useState(false);
  const [capabilityAreas, setCapabilityAreas] = useState<string[]>([]);
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [capabilityAreasLoading, setCapabilityAreasLoading] = useState(false);
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(false);
  const [selectedCapabilityAreas, setSelectedCapabilityAreas] = useState<string[]>([]);
  const [selectedCapabilities, setSelectedCapabilities] = useState<string[]>([]);
  const [originalCapabilityAreas, setOriginalCapabilityAreas] = useState<string[]>([]);
  const [originalCapabilities, setOriginalCapabilities] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showMoreCaseStudies, setShowMoreCaseStudies] = useState(false);
  const [showCapabilityAreaDropdown, setShowCapabilityAreaDropdown] = useState(false);
  const [showCapabilityDropdown, setShowCapabilityDropdown] = useState(false);
  
  // Filter states
  const [filterCapabilityArea, setFilterCapabilityArea] = useState<string>("");
  const [filterGeography, setFilterGeography] = useState<string>("");
  const [filterPoC, setFilterPoC] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.custom-dropdown-wrapper')) {
        setShowCapabilityAreaDropdown(false);
        setShowCapabilityDropdown(false);
      }
    };

    if (showCapabilityAreaDropdown || showCapabilityDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCapabilityAreaDropdown, showCapabilityDropdown]);

  useEffect(() => {
    console.log("[CaseStudy] useEffect triggered - pu value:", pu);
    // Fetch case study data when projectid or setname changes
    if (projectid && setname) {
      fetchCaseStudyData(projectid, setname);
    }
  }, [projectid, setname, pu]);

  const getUserFromSession = (): string => {
    return sessionStorage.getItem("PBT_EMAIL") || "";
  };

  // Add helper function to map PU to practice_unit_id
  const getPracticeUnitId = (puValue: string | undefined): number => {
    console.log("[getPracticeUnitId] Input puValue:", puValue, "Type:", typeof puValue);
    if (!puValue) {
      console.warn("[getPracticeUnitId] ⚠️ puValue is undefined/null/empty, defaulting to 2");
      return 2; // Default to Quality Engineering (TT)
    }
    const normalized = puValue.toUpperCase().trim();
    const result = normalized === 'DL' ? 1 : 2;
    console.log("[getPracticeUnitId] Normalized:", normalized, "→ practice_unit_id:", result);
    return result; // DL = Digital Engineering (1), TT = Quality Engineering (2)
  };

  const isNAValue = (value: string | string[]): boolean => {
    if (Array.isArray(value)) {
      return value.length === 0 || value.some(v => 
        v && typeof v === 'string' && 
        (v.toUpperCase() === "N/A" || v.toUpperCase() === "NA")
      );
    }
    return !value || (typeof value === 'string' && 
      (value.toUpperCase() === "N/A" || value.toUpperCase() === "NA"));
  };

  const sanitizeArray = (val: any): string[] => {
    if (!val) return [];
    if (Array.isArray(val)) return val.filter(v => !!v && v !== 'N/A' && v !== 'NA');
    if (typeof val === 'string') {
      const trimmed = val.trim();
      if (trimmed === '' || trimmed.toUpperCase() === 'N/A' || trimmed.toUpperCase() === 'NA') return [];
      return [trimmed];
    }
    return [];
  };

  const fetchProjectDetails = async (projectId: string, setName: string): Promise<ProjectDetails | null> => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_DASHBOARD_SERVICE_BASE_URL}/project-details/${projectId}/${setName}`
      );
      console.log("[CaseStudy] Project details raw response keys:", Object.keys(response.data || {}));
      console.log("[CaseStudy] Project details full response:", response.data);
      const raw = response.data || {};
      // Fallback DU-to-Industry map (mirrors logic from CreateProject)
      const DU_TO_INDUSTRY: Record<string, string> = {
        BB: "BANKING & FINANCIAL SERVICES",
        CSU: "BANKING & FINANCIAL SERVICES",
        DSA: "BANKING & FINANCIAL SERVICES",
        BC: "BANKING & FINANCIAL SERVICES",
        ND: "BANKING & FINANCIAL SERVICES",
        BF: "BANKING & FINANCIAL SERVICES",
        EG: "ENERGY",
        GP: "ENERGY",
        EY: "ENERGY",
        HA: "HEALTH",
        HE: "HEALTH",
        HT: "HI-TECH",
        HH: "HI-TECH",
        NN: "INSURANCE",
        DL: "INSURANCE",
        NC: "INSURANCE",
        NS: "INSURANCE",
        PN: "INSURANCE",
        LS: "LIFE SCIENCES",
        MM: "MANUFACTURING",
        MN: "MANUFACTURING",
        ME: "MEDIA & ENTERTAINMENT",
        MD: "MEDIA & ENTERTAINMENT",
        FT: "MICROSOFT",
        RR: "RETAIL & CONSUMER PACKAGED GOODS",
        RE: "RETAIL & CONSUMER PACKAGED GOODS",
        TW: "TRAVEL TRANSPORT & HOSPITALITY",
        DATA: "TRAVEL TRANSPORT & HOSPITALITY",
        UL: "UTILITIES",
        UA: "UTILITIES"
      };

      // Identify DU/PU from multiple potential keys
      const duValue: string = (
        raw.pu || raw.u_practice_unit || raw.du || raw.delivery_unit || raw.u_delivery_unit || raw.DU || raw.PU || ''
      ).toString().trim().toUpperCase();
      
      console.log("[CaseStudy] DU/PU value resolved:", duValue);

      // Attempt to gather industry from multiple possible fields
      const industryCandidates: string[] = [
        raw.mapped_industry,
        raw.industry,
        raw.mappedIndustry,
        raw.MAPPED_INDUSTRY,
        raw.MappedIndustry,
      ].filter(v => typeof v === 'string' && v.trim() !== '') as string[];

      let mapped_industry: string = industryCandidates.length > 0 ? industryCandidates[0].toString().trim() : '';
      const originalIndustry = mapped_industry;

      // If missing or N/A, derive from DU mapping
      if (!mapped_industry || /^N\/?A$/i.test(mapped_industry)) {
        mapped_industry = DU_TO_INDUSTRY[duValue] || 'N/A';
      }

      console.log(`[CaseStudy] DU value resolved: '${duValue}', original industry: '${originalIndustry}', final industry: '${mapped_industry}'`);
      return {
        projectid: raw.projectid || projectId,
        set_name: raw.set_name || setName,
        account: raw.account || raw.account_name || '',
        mapped_industry: mapped_industry === '' ? 'N/A' : mapped_industry,
        capability_area: sanitizeArray(raw.capability_area),
        capability: sanitizeArray(raw.capability),
        du: duValue
      };
    } catch (error) {
      console.error("Error fetching project details:", error);
      throw error;
    }
  };

  const fetchCaseStudyFilters = async (projectDetails: ProjectDetails) => {
    try {
      const practiceUnitId = getPracticeUnitId(pu);
      
      const basePayload = {
        practice_unit_id: practiceUnitId,
        username: getUserFromSession(),
        capabilityarea: isNAValue(projectDetails.capability_area) ? [] : projectDetails.capability_area,
        capabilities: isNAValue(projectDetails.capability) ? [] : projectDetails.capability,
        industry: isNAValue(projectDetails.mapped_industry) ? [] : [projectDetails.mapped_industry],
        account: isNAValue(projectDetails.account) ? [] : [projectDetails.account],
        geography: [],
        casestudyfilename: [],
        startmonth: null,
        startyear: null,
        endmonth: null,
        endyear: null,
        teamsize: "",
        teamsizeoperator: ""
      };

      console.log("Case study filters payload:", basePayload);

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_CASE_STUDY_SERVICE_BASE_URL}/casestudy/filters`,
        basePayload,
        {
          headers: {
            "X-API-KEY": "PBTteamkey",
            "Content-Type": "application/json"
          }
        }
      );

      console.log("Case study filters response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching case study filters:", error);
      throw error;
    }
  };

  const processApiResponse = (data: any[]): any[] => {
    if (!Array.isArray(data)) return [];
    return data
      .map(item => {
        const key = Object.keys(item)[0];
        if (!key) return null;
        const caseStudyData = item[key];
        // Add the key as the case study identifier for easier access
        return {
          ...caseStudyData,
          case_study_key: key
        };
      })
      .filter(Boolean);
  };

  const handleCaseStudyNameClick = async (caseStudy: any) => {
    if (caseStudy.case_study_file_link) {
      try {
        console.log("Downloading PPT for case study:", caseStudy.case_study_name);
        
        const payload = {
          file_path: caseStudy.case_study_file_link
        };

        const response = await axiosInstance.post(
          `${process.env.REACT_APP_CASE_STUDY_SERVICE_BASE_URL}/casestudy/downloadppt`,
          payload,
          {
            headers: {
              "X-API-KEY": "PBTteamkey",
              "Content-Type": "application/json"
            }
          }
        );

        console.log("Download PPT response:", response.data);
        
        // Assuming the API returns a download URL in the response
        const downloadUrl = response.data.downloadUrl || response.data.url || response.data;
        
        if (downloadUrl) {
          // Open the download URL in a new tab
          window.open(downloadUrl, '_blank', 'noopener,noreferrer');
        } else {
          console.warn("No download URL received from API");
          setMessageBox({ 
            show: true, 
            message: "Unable to get download URL for this case study" 
          });
        }
      } catch (error) {
        console.error("Error downloading PPT:", error);
        setMessageBox({ 
          show: true, 
          message: "Error downloading case study file. Please try again." 
        });
      }
    } else {
      console.warn("No file link available for this case study");
      setMessageBox({ 
        show: true, 
        message: "No file link available for this case study" 
      });
    }
  };

  const formatDate = (dateString: string): string => {
    if (!dateString || dateString === "N/A") return "N/A";
    try {
      const date = new Date(dateString);
      if (date.getFullYear() === 9999) return "Ongoing";
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  const fetchCaseStudyData = async (projectId: string, setName: string) => {
    try {
      setLoading(true);
      setError("");
      
      console.log("Fetching case study data for project:", projectId, "and set:", setName);
      
      // Step 1: Fetch project details
      const details = await fetchProjectDetails(projectId, setName);
      if (!details) {
        throw new Error("Failed to fetch project details");
      }
      
      setProjectDetails(details);
      
      // Step 2: Fetch case study filters using project details
      const filtersData = await fetchCaseStudyFilters(details);
      
      // Step 3: Process the response
      const processedData = processApiResponse(filtersData);
      setCaseStudyData(processedData);
      
      // Step 4: Update case study count with file links
      await updateCaseStudyCount(projectId, setName, processedData);
      
    } catch (error: any) {
      console.error("Error fetching case study data:", error);
      setError(error.message || "Failed to fetch case study data");
      setCaseStudyData([]);
    } finally {
      setLoading(false);
    }
  };

  // Add helper function to check if PU is DL or TT
  const isEditablePU = () => {
    const normalizedPU = (pu ?? "").trim().toUpperCase();
    return normalizedPU === "DL" || normalizedPU === "TT";
  };

  // Function to update case study count
  const updateCaseStudyCount = async (projectId: string, setName: string, caseStudies: any[]) => {
    try {
      // Extract case_study_file_link from case studies
      const fileLinks = caseStudies
        .filter(cs => cs.case_study_file_link)
        .map(cs => cs.case_study_file_link);

      // Always call the PATCH API, even if no file links (count will be 0)
      console.log("Updating case study count with file links:", fileLinks.length > 0 ? fileLinks : "No file links (count: 0)");

      await axiosInstance.patch(
        `${process.env.REACT_APP_ASSESSMENT_SERVICE_BASE_URL}/update-casestudy-count/${projectId}/${setName}`,
        { case_study_file_links: fileLinks },
        {
          headers: {
            "X-API-KEY": "PBTteamkey",
            "Content-Type": "application/json"
          }
        }
      );

      console.log(`Case study count updated successfully (count: ${fileLinks.length})`);
    } catch (error) {
      console.error("Error updating case study count:", error);
      // Don't throw error, just log it - this shouldn't block the UI
    }
  };

  // Function to fetch capability areas
  const fetchCapabilityAreas = async () => {
    const normalizedPU = (pu ?? "").trim().toUpperCase();
    const search = normalizedPU === "DL" ? "Digital Engineering" : normalizedPU === "TT" ? "Quality Engineering" : null;

    if (!search) return;

    setCapabilityAreasLoading(true);
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_CASE_STUDY_SERVICE_BASE_URL}/casestudy/capabilityarea`,
        { search },
        { headers: { "X-API-KEY": "PBTteamkey" } }
      );

      const extractList = (payload: any): string[] => {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.result)) return payload.result;
        if (Array.isArray(payload?.data)) return payload.data;
        const values = Object.values(payload ?? {});
        if (values.length > 0 && Array.isArray(values[0])) return values[0] as string[];
        return [];
      };

      const areas = extractList(response.data);
      setCapabilityAreas(areas);
    } catch (error) {
      console.error("Error fetching capability areas:", error);
      setCapabilityAreas([]);
    } finally {
      setCapabilityAreasLoading(false);
    }
  };

  // Function to fetch capabilities based on selected capability areas
  const fetchCapabilities = async (areas: string[]) => {
    if (!areas || areas.length === 0) {
      setCapabilities([]);
      return;
    }

    setCapabilitiesLoading(true);
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_CASE_STUDY_SERVICE_BASE_URL}/casestudy/capability`,
        { capabilityAreas: areas },
        { headers: { "X-API-KEY": "PBTteamkey" } }
      );

      const extractList = (payload: any): string[] => {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.result)) return payload.result;
        if (Array.isArray(payload?.data)) return payload.data;
        const values = Object.values(payload ?? {});
        if (values.length > 0 && Array.isArray(values[0])) return values[0] as string[];
        return [];
      };

      const caps = Array.from(new Set(extractList(response.data)));
      setCapabilities(caps);
    } catch (error) {
      console.error("Error fetching capabilities:", error);
      setCapabilities([]);
    } finally {
      setCapabilitiesLoading(false);
    }
  };

  // Handle Edit button click
  const handleEditClick = async () => {
    setIsEditing(true);
    // Set current values from project details and store originals
    if (projectDetails) {
      const areas = projectDetails.capability_area || [];
      const caps = projectDetails.capability || [];
      setSelectedCapabilityAreas([...areas]);
      setSelectedCapabilities([...caps]);
      setOriginalCapabilityAreas([...areas]);
      setOriginalCapabilities([...caps]);
    }
    // Fetch capability areas
    await fetchCapabilityAreas();
    // If there are existing capability areas, fetch capabilities
    if (projectDetails?.capability_area && projectDetails.capability_area.length > 0) {
      await fetchCapabilities(projectDetails.capability_area);
    }
  };

  // Handle Cancel button click
  const handleCancelClick = () => {
    setIsEditing(false);
    setSelectedCapabilityAreas([]);
    setSelectedCapabilities([]);
    setOriginalCapabilityAreas([]);
    setOriginalCapabilities([]);
    setCapabilityAreas([]);
    setCapabilities([]);
    setShowCapabilityAreaDropdown(false);
    setShowCapabilityDropdown(false);
  };

  // Handle Save button click
  const handleSaveClick = async () => {
    if (!projectid || !setname) {
      toast.error("Project ID or Set Name is missing");
      return;
    }

    // Check if values have changed
    const areasChanged = JSON.stringify([...selectedCapabilityAreas].sort()) !== JSON.stringify([...originalCapabilityAreas].sort());
    const capsChanged = JSON.stringify([...selectedCapabilities].sort()) !== JSON.stringify([...originalCapabilities].sort());

    if (!areasChanged && !capsChanged) {
      toast.warning("Please change capability area or capability to save, or click cancel");
      return;
    }

    try {
      setIsSaving(true);
      setLoading(true);

      // Step 1: Save capability area and capability to dashboard service
      console.log("Saving capability area and capability:", {
        capability_area: selectedCapabilityAreas,
        capability: selectedCapabilities
      });

      await axiosInstance.patch(
        `${process.env.REACT_APP_DASHBOARD_SERVICE_BASE_URL}/project-capabilities/${projectid}/${setname}`,
        {
          capability_area: selectedCapabilityAreas,
          capability: selectedCapabilities
        },
        {
          headers: {
            "X-API-KEY": "PBTteamkey",
            "Content-Type": "application/json"
          }
        }
      );

      toast.success("Capability area and capability edited and saved successfully");

      // Step 2: Fetch updated project details
      console.log("Fetching updated project details...");
      const projectDetailsResponse = await axiosInstance.get(
        `${process.env.REACT_APP_DASHBOARD_SERVICE_BASE_URL}/project-details/${projectid}/${setname}`,
        {
          headers: {
            "X-API-KEY": "PBTteamkey"
          }
        }
      );

      const updatedDetails = projectDetailsResponse.data;
      console.log("Updated project details:", updatedDetails);

      // Step 3: Prepare and call filter API with updated details
      const practiceUnitId = getPracticeUnitId(pu);
      const filterPayload = {
        practice_unit_id: practiceUnitId,
        username: getUserFromSession(),
        capabilityarea: selectedCapabilityAreas,
        capabilities: selectedCapabilities,
        industry: isNAValue(updatedDetails.mapped_industry) ? [] : [updatedDetails.mapped_industry],
        account: isNAValue(updatedDetails.account) ? [] : [updatedDetails.account],
        geography: [],
        casestudyfilename: [],
        startmonth: null,
        startyear: null,
        endmonth: null,
        endyear: null,
        teamsize: "",
        teamsizeoperator: ""
      };

      console.log("Calling filter API with payload:", filterPayload);
      const filtersResponse = await axiosInstance.post(
        `${process.env.REACT_APP_CASE_STUDY_SERVICE_BASE_URL}/casestudy/filters`,
        filterPayload,
        {
          headers: {
            "X-API-KEY": "PBTteamkey",
            "Content-Type": "application/json"
          }
        }
      );

      console.log("Filter API response:", filtersResponse.data);

      // Step 4: Process the response
      const processedData = processApiResponse(filtersResponse.data);
      setCaseStudyData(processedData);

      // Step 5: Update case study count
      await updateCaseStudyCount(projectid, setname, processedData);

      // Step 6: Update project details state
      const updatedProjectDetails: ProjectDetails = {
        projectid: updatedDetails.projectid || projectid,
        set_name: updatedDetails.set_name || setname,
        account: updatedDetails.account || '',
        mapped_industry: updatedDetails.mapped_industry || 'N/A',
        capability_area: selectedCapabilityAreas,
        capability: selectedCapabilities,
        du: updatedDetails.du || updatedDetails.pu || ''
      };
      setProjectDetails(updatedProjectDetails);

      // Exit edit mode and clear states
      setIsEditing(false);
      setSelectedCapabilityAreas([]);
      setSelectedCapabilities([]);
      setOriginalCapabilityAreas([]);
      setOriginalCapabilities([]);
      setCapabilityAreas([]);
      setCapabilities([]);

    } catch (error: any) {
      console.error("Error saving capability data:", error);
      toast.error(error.response?.data?.message || "Failed to save capability data. Please try again.");
    } finally {
      setIsSaving(false);
      setLoading(false);
    }
  };

  // Handle capability area selection with checkbox
  const handleCapabilityAreaToggle = async (area: string) => {
    let newAreas: string[];
    if (selectedCapabilityAreas.includes(area)) {
      newAreas = selectedCapabilityAreas.filter(a => a !== area);
    } else {
      newAreas = [...selectedCapabilityAreas, area];
    }
    setSelectedCapabilityAreas(newAreas);
    setSelectedCapabilities([]); // Reset capabilities when areas change
    if (newAreas.length > 0) {
      await fetchCapabilities(newAreas);
    } else {
      setCapabilities([]);
    }
  };

  // Select/Deselect all capability areas
  const handleSelectAllCapabilityAreas = async () => {
    if (selectedCapabilityAreas.length === capabilityAreas.length) {
      // Deselect all
      setSelectedCapabilityAreas([]);
      setSelectedCapabilities([]);
      setCapabilities([]);
    } else {
      // Select all
      setSelectedCapabilityAreas([...capabilityAreas]);
      setSelectedCapabilities([]);
      await fetchCapabilities([...capabilityAreas]);
    }
  };

  // Handle capability selection with checkbox
  const handleCapabilityToggle = (capability: string) => {
    if (selectedCapabilities.includes(capability)) {
      setSelectedCapabilities(selectedCapabilities.filter(c => c !== capability));
    } else {
      setSelectedCapabilities([...selectedCapabilities, capability]);
    }
  };

  // Select/Deselect all capabilities
  const handleSelectAllCapabilities = () => {
    if (selectedCapabilities.length === capabilities.length) {
      // Deselect all
      setSelectedCapabilities([]);
    } else {
      // Select all
      setSelectedCapabilities([...capabilities]);
    }
  };

  // Remove capability area
  const removeCapabilityArea = (areaToRemove: string) => {
    const newAreas = selectedCapabilityAreas.filter(area => area !== areaToRemove);
    setSelectedCapabilityAreas(newAreas);
    setSelectedCapabilities([]); // Reset capabilities
    if (newAreas.length > 0) {
      fetchCapabilities(newAreas);
    } else {
      setCapabilities([]);
    }
  };

  // Remove capability
  const removeCapability = (capabilityToRemove: string) => {
    setSelectedCapabilities(selectedCapabilities.filter(cap => cap !== capabilityToRemove));
  };

  if (loading) {
    return (
      <div className="case-study-container">
        <div className="loading">{isSaving ? "Saving and refreshing case study data..." : "Loading case study data..."}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="case-study-container">
        <div className="error">
          <p>Error: {error}</p>
          <button onClick={() => projectid && setname && fetchCaseStudyData(projectid, setname)}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Filter case studies based on selected filters and search
  const filteredCaseStudies = caseStudyData.filter(caseStudy => {
    const matchesCapabilityArea = !filterCapabilityArea || 
      (Array.isArray(caseStudy.capability_area) && caseStudy.capability_area.includes(filterCapabilityArea)) ||
      caseStudy.capability_area === filterCapabilityArea;
    
    const matchesGeography = !filterGeography || caseStudy.geography === filterGeography;
    
    const matchesPoC = !filterPoC || 
      (caseStudy.case_study_poc && caseStudy.case_study_poc.toString().toLowerCase().includes(filterPoC.toLowerCase()));
    
    const matchesSearch = !searchTerm || 
      caseStudy.case_study_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (Array.isArray(caseStudy.capabilities) && caseStudy.capabilities.some((cap: string) => cap.toLowerCase().includes(searchTerm.toLowerCase())));
    
    return matchesCapabilityArea && matchesGeography && matchesPoC && matchesSearch;
  });

  // Get unique values for filter dropdowns
  const uniqueCapabilityAreas = Array.from(new Set(
    caseStudyData.flatMap(cs => Array.isArray(cs.capability_area) ? cs.capability_area : [cs.capability_area]).filter(Boolean)
  ));
  
  const uniqueGeographies = Array.from(new Set(
    caseStudyData.map(cs => cs.geography).filter(Boolean)
  ));

  // Download handler
  const handleDownload = () => {
    toast.info("Download functionality coming soon!");
  };

  return (
    <div className="case-study-wrapper-new">
      {/* Horizontal Compact Project Overview */}
      <div className="project-overview-compact">
        <div className="overview-content">
          <div className="overview-row">
            <span className="label">ACCOUNT:</span>
            <span className="value">{projectDetails?.account || 'N/A'}</span>
          </div>
          <div className="overview-row">
            <span className="label">INDUSTRY:</span>
            <span className="value">{(() => {
              if (!projectDetails) return 'N/A';
              const ind = projectDetails.mapped_industry || '';
              if (!ind || /^N\/?A$/i.test(ind)) {
                const DU_TO_INDUSTRY: Record<string, string> = {
                  BB: "BANKING & FINANCIAL SERVICES", CSU: "BANKING & FINANCIAL SERVICES",
                  DSA: "BANKING & FINANCIAL SERVICES", BC: "BANKING & FINANCIAL SERVICES",
                  ND: "BANKING & FINANCIAL SERVICES", BF: "BANKING & FINANCIAL SERVICES",
                  EG: "ENERGY", GP: "ENERGY", EY: "ENERGY", HA: "HEALTH", HE: "HEALTH",
                  HT: "HI-TECH", HH: "HI-TECH", NN: "INSURANCE", DL: "INSURANCE",
                  NC: "INSURANCE", NS: "INSURANCE", PN: "INSURANCE", LS: "LIFE SCIENCES",
                  MM: "MANUFACTURING", MN: "MANUFACTURING", ME: "MEDIA & ENTERTAINMENT",
                  MD: "MEDIA & ENTERTAINMENT", FT: "MICROSOFT",
                  RR: "RETAIL & CONSUMER PACKAGED GOODS", RE: "RETAIL & CONSUMER PACKAGED GOODS",
                  TW: "TRAVEL TRANSPORT & HOSPITALITY", DATA: "TRAVEL TRANSPORT & HOSPITALITY",
                  UL: "UTILITIES", UA: "UTILITIES"
                };
                return projectDetails.du ? (DU_TO_INDUSTRY[projectDetails.du] || 'N/A') : 'N/A';
              }
              return ind;
            })()}</span>
          </div>
          <div className="overview-row">
            <span className="label">CAPABILITY AREA:</span>
            {!isEditing ? (
              <span className="value capability-display">
                {projectDetails?.capability_area && projectDetails.capability_area.length > 0 ? (
                  projectDetails.capability_area.map((area, idx) => (
                    <span key={idx} className="capability-tag-inline">{area}</span>
                  ))
                ) : 'N/A'}
              </span>
            ) : (
              <div className="edit-inline">
                <Autocomplete
                  multiple
                  id="capability-area-select"
                  options={["All", ...capabilityAreas]}
                  disableCloseOnSelect
                  value={selectedCapabilityAreas}
                  loading={capabilityAreasLoading}
                  onChange={(event, newValue, reason, details) => {
                    if (details?.option === "All") {
                      if (selectedCapabilityAreas.length === capabilityAreas.length) {
                        // Deselect all
                        setSelectedCapabilityAreas([]);
                        setSelectedCapabilities([]);
                        setCapabilities([]);
                      } else {
                        // Select all
                        setSelectedCapabilityAreas([...capabilityAreas]);
                        setSelectedCapabilities([]);
                        fetchCapabilities([...capabilityAreas]);
                      }
                    } else {
                      const filtered = newValue.filter(v => v !== "All");
                      setSelectedCapabilityAreas(filtered);
                      setSelectedCapabilities([]);
                      if (filtered.length > 0) {
                        fetchCapabilities(filtered);
                      } else {
                        setCapabilities([]);
                      }
                    }
                  }}
                  getOptionLabel={(option) => option}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        style={{ marginRight: 8 }}
                        checked={option === "All" ? selectedCapabilityAreas.length === capabilityAreas.length && capabilityAreas.length > 0 : selected}
                      />
                      {option}
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={selectedCapabilityAreas.length === 0 ? "Select Capability Area" : ""}
                      size="small"
                    />
                  )}
                  limitTags={2}
                  componentsProps={{
                    popper: {
                      placement: 'bottom-start',
                      modifiers: [
                        {
                          name: 'flip',
                          enabled: false
                        },
                        {
                          name: 'preventOverflow',
                          enabled: true,
                          options: {
                            altAxis: true,
                            tether: false
                          }
                        }
                      ]
                    }
                  }}
                  PopperComponent={(props) => <Popper {...props} style={{ position: 'fixed' }} />}
                  sx={{ 
                    width: 300,
                    maxWidth: 300,
                    '& .MuiAutocomplete-inputRoot': {
                      flexWrap: 'wrap',
                      maxHeight: 100,
                      overflow: 'auto'
                    }
                  }}
                />
              </div>
            )}
          </div>
          <div className="overview-row">
            <span className="label">CAPABILITY:</span>
            {!isEditing ? (
              <span className="value capability-display">
                {projectDetails?.capability && projectDetails.capability.length > 0 ? (
                  projectDetails.capability.map((cap, idx) => (
                    <span key={idx} className="capability-tag-inline">{cap}</span>
                  ))
                ) : 'N/A'}
              </span>
            ) : (
              <div className="edit-inline">
                <Autocomplete
                  multiple
                  id="capability-select"
                  options={["All", ...capabilities]}
                  disableCloseOnSelect
                  value={selectedCapabilities}
                  loading={capabilitiesLoading}
                  disabled={selectedCapabilityAreas.length === 0 || capabilities.length === 0}
                  onChange={(event, newValue, reason, details) => {
                    if (details?.option === "All") {
                      if (selectedCapabilities.length === capabilities.length) {
                        // Deselect all
                        setSelectedCapabilities([]);
                      } else {
                        // Select all
                        setSelectedCapabilities([...capabilities]);
                      }
                    } else {
                      const filtered = newValue.filter(v => v !== "All");
                      setSelectedCapabilities(filtered);
                    }
                  }}
                  getOptionLabel={(option) => option}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        style={{ marginRight: 8 }}
                        checked={option === "All" ? selectedCapabilities.length === capabilities.length && capabilities.length > 0 : selected}
                      />
                      {option}
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={
                        selectedCapabilityAreas.length === 0 
                          ? "Select Capability Area first" 
                          : selectedCapabilities.length === 0 
                            ? "Select Capability" 
                            : ""
                      }
                      size="small"
                    />
                  )}
                  limitTags={2}
                  componentsProps={{
                    popper: {
                      placement: 'bottom-start',
                      modifiers: [
                        {
                          name: 'flip',
                          enabled: false
                        },
                        {
                          name: 'preventOverflow',
                          enabled: true,
                          options: {
                            altAxis: true,
                            tether: false
                          }
                        }
                      ]
                    }
                  }}
                  PopperComponent={(props) => <Popper {...props} style={{ position: 'fixed' }} />}
                  sx={{ 
                    width: 300,
                    maxWidth: 300,
                    '& .MuiAutocomplete-inputRoot': {
                      flexWrap: 'wrap',
                      maxHeight: 100,
                      overflow: 'auto'
                    }
                  }}
                />
              </div>
            )}
          </div>
        </div>
        {isEditablePU() && (
          <div className="overview-actions">
            {!isEditing ? (
              <button onClick={handleEditClick} className="btn-edit-compact">Edit</button>
            ) : (
              <div className="edit-buttons">
                <button onClick={handleSaveClick} disabled={isSaving} className="btn-save-compact">
                  {isSaving ? 'Applying...' : 'Apply'}
                </button>
                <button onClick={handleCancelClick} disabled={isSaving} className="btn-cancel-compact">
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Main Content Area */}
      {caseStudyData.length === 0 && !loading ? (
        <div className="info-message-centered">
          <p>Case-Studies are currently available for Digital Engineering (DL) & Quality Engineering (TT)</p>
        </div>
      ) : (
        <div className="case-study-content-new">
          {/* Enhanced Table */}
          <div className="table-container-enhanced">
            <table className="case-study-table-enhanced">
              <thead>
                <tr>
                  <th className="col-header-enhanced" colSpan={7}>
                    <div className="table-header-content">
                      <span className="table-title">Top 3 Relevant Case Studies</span>
                      {filteredCaseStudies.length > 3 && (
                        <button 
                          type="button"
                          className="btn-show-all-header"
                          onClick={() => setShowMoreCaseStudies(!showMoreCaseStudies)}
                        >
                          {showMoreCaseStudies ? 'Show Top 3' : `Show All ${filteredCaseStudies.length} Case Studies`}
                        </button>
                      )}
                    </div>
                  </th>
                </tr>
                <tr>
                  <th className="col-case-name">Case Study Name</th>
                  <th className="col-capability-area">Capability Area</th>
                  <th className="col-capability-enhanced">Capability</th>
                  <th className="col-geography-enhanced">Geography</th>
                  <th className="col-poc">PoC Name(s)</th>
                  <th className="col-team-size">Team Size</th>
                  <th className="col-summary">Summary</th>
                </tr>
              </thead>
              <tbody>
                {filteredCaseStudies.slice(0, showMoreCaseStudies ? filteredCaseStudies.length : 3).map((caseStudy, index) => (
                  <tr key={caseStudy.case_study_id || index} className="table-row-enhanced">
                    <td className="col-case-name">
                      <div className="case-name-cell">
                        <span className="doc-icon-enhanced">📄</span>
                        {caseStudy.case_study_file_link ? (
                          <span 
                            className="case-name-link"
                            onClick={() => handleCaseStudyNameClick(caseStudy)}
                            title="Click to open case study file"
                          >
                            {caseStudy.case_study_name || "N/A"}
                          </span>
                        ) : (
                          <span className="case-name-text">{caseStudy.case_study_name || "N/A"}</span>
                        )}
                      </div>
                    </td>
                    <td className="col-capability-area">
                      {Array.isArray(caseStudy.capability_area) 
                        ? caseStudy.capability_area.join(", ") 
                        : (caseStudy.capability_area || "N/A")}
                    </td>
                    <td className="col-capability-enhanced">
                      {Array.isArray(caseStudy.capabilities) 
                        ? caseStudy.capabilities.join(", ") 
                        : (caseStudy.capabilities || "N/A")}
                    </td>
                    <td className="col-geography-enhanced">
                      <div className="geography-cell-enhanced">
                        <span className="geo-icon-enhanced">📍</span>
                        {caseStudy.geography || "N/A"}
                      </div>
                    </td>
                    <td className="col-poc">
                      {caseStudy.case_study_poc || caseStudy.poc || "N/A"}
                    </td>
                    <td className="col-team-size">
                      {caseStudy.team_size || caseStudy.teamSize || "N/A"}
                    </td>
                    <td className="col-summary">
                      <div className="summary-cell" title={caseStudy.case_study_summary || "No summary available"}>
                        {caseStudy.case_study_summary 
                          ? (caseStudy.case_study_summary.length > 80 
                            ? `${caseStudy.case_study_summary.substring(0, 80)}...` 
                            : caseStudy.case_study_summary)
                          : "N/A"}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Custom Message Box */}
      {messageBox.show && (
        <div 
          className="message-overlay" 
          onClick={() => setMessageBox({ show: false, message: "" })}
        >
          <div className="message-box">
            <p>{messageBox.message}</p>
          </div>
        </div>
      )}
    </div>
  );

  function handleViewCaseStudy(caseStudy: any) {
    console.log("Viewing case study:", caseStudy);
    const message = `Case Study Summary: ${caseStudy.case_study_summary || "Unknown"}`;
    setMessageBox({ show: true, message });
  }
};

export default CaseStudy;